import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Avatar } from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Dialog from '@givebutter/primitives/dialog';
import { useToast } from '@givebutter/primitives/toast';
import { faUnlock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TwoFactorProvider, requestTwoFactorCode, verifyTwoFactorCode } from 'api/twoFactor';
import * as Form from 'components/form';
import { GivebutterAxiosError } from 'types/error';

interface TwoFactorChallengeDialogProps extends Dialog.DialogProps {
    twoFactorMethods?: Record<string, string>;
    onSuccess?: () => void;
}

interface TwoFactorChallengeFormValues {
    code: string;
}

const TwoFactorChallengeDialog = ({
    onSuccess,
    onOpenChange: onOpenChangeConsumer,
    twoFactorMethods,
    ...rest
}: TwoFactorChallengeDialogProps) => {
    const { error, success } = useToast();

    const [hasSentCode, setHasSentCode] = useState(false);
    const [provider, setProvider] = useState<TwoFactorProvider>('email');
    const [contactMethod, setContactMethod] = useState<string | undefined>();

    const methods = Form.useGivebutterForm<TwoFactorChallengeFormValues>({
        defaultValues: {
            code: '',
        },
    });
    const {
        handleSubmit,
        control,
        path,
        watch,
        trigger,
        formState: { isDirty },
    } = methods;
    const [code] = watch(['code']);

    const { mutate: submitVerification, isPending: isSubmittingVerification } = useMutation({
        mutationFn: ({ code }: TwoFactorChallengeFormValues) => verifyTwoFactorCode(provider, code),
        onSuccess: () => {
            onSuccess?.();
            success('Success!');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error');
        },
    });

    const { mutate: requestCode, isPending: isRequestingCode } = useMutation({
        mutationFn: (provider: 'email' | 'sms') => requestTwoFactorCode(provider),
        onSuccess: () => {
            setHasSentCode(true);
            success('Code sent!');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error');
        },
    });

    const description = hasSentCode
        ? `We sent a verification code to ${contactMethod}.`
        : "We need to make sure it's really you before continuing.";

    useEffect(() => {
        if (isDirty) {
            trigger('code').then(isValid => {
                if (isValid) {
                    submitVerification({ code });
                }
            });
        }
    }, [code, submitVerification, trigger, isDirty]);

    return (
        <Dialog.Root
            onOpenChange={open => {
                onOpenChangeConsumer?.(open);
            }}
            {...rest}
        >
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Header>
                        <Avatar className="bg-gray-100">
                            <FontAwesomeIcon icon={faUnlock} className="text-2xl text-gray-400" />
                        </Avatar>
                        <Dialog.Close />
                    </Dialog.Header>
                    <Dialog.Body>
                        <div className="flex flex-col gap-1">
                            <Dialog.Title>Two Factor Verification</Dialog.Title>
                            <Dialog.Description>{description}</Dialog.Description>
                        </div>
                        <Form.GivebutterFormProvider {...methods}>
                            <form
                                onSubmit={handleSubmit(
                                    submitVerification as SubmitHandler<TwoFactorChallengeFormValues>,
                                )}
                                className="flex flex-col gap-4"
                            >
                                {hasSentCode && (
                                    <div className="flex flex-col gap-2">
                                        <Form.Container>
                                            <Form.TextInputRoot
                                                control={control}
                                                name={path('code')}
                                                inputRootProps={{
                                                    mask: 'verification-code',
                                                }}
                                                disabled={isSubmittingVerification}
                                                rules={{
                                                    required: 'Code is required',
                                                    minLength: {
                                                        value: 6,
                                                        message: 'Code must be 6 characters',
                                                    },
                                                    maxLength: {
                                                        value: 6,
                                                        message: 'Code must be 6 characters',
                                                    },
                                                }}
                                            >
                                                <Form.TextInputForm placeholder="Verification code" />
                                            </Form.TextInputRoot>
                                            <Form.Error name={path('code')} />
                                        </Form.Container>
                                        <Form.Container>
                                            <Button
                                                variant="none"
                                                color="primary"
                                                className="font-medium"
                                                onClick={() => requestCode(provider)}
                                                disabled={isRequestingCode}
                                            >
                                                Resend code
                                            </Button>
                                        </Form.Container>
                                    </div>
                                )}
                                {!hasSentCode && (
                                    <div className="flex flex-col gap-4">
                                        {twoFactorMethods?.SMS && (
                                            <Box className="flex w-full items-center justify-between gap-6 p-4">
                                                <div className="flex flex-col gap-1">
                                                    <p className="font-medium">
                                                        Text Message (SMS)
                                                    </p>
                                                    <p className="text-gray-600">
                                                        {twoFactorMethods.SMS}
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() => {
                                                        requestCode('sms');
                                                        setProvider('sms');
                                                        setContactMethod(twoFactorMethods.SMS);
                                                    }}
                                                    disabled={isRequestingCode}
                                                >
                                                    Send code
                                                </Button>
                                            </Box>
                                        )}
                                        {twoFactorMethods?.Email && (
                                            <Box className="flex w-full items-center justify-between gap-6 p-4">
                                                <div className="flex flex-col gap-1">
                                                    <p className="font-medium">Email</p>
                                                    <p className="text-gray-600">
                                                        {twoFactorMethods.Email}
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() => {
                                                        requestCode('email');
                                                        setProvider('email');
                                                        setContactMethod(twoFactorMethods.Email);
                                                    }}
                                                    disabled={isRequestingCode}
                                                >
                                                    Send code
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                )}
                                <Form.SubmitButton className="hidden" />
                            </form>
                        </Form.GivebutterFormProvider>
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export { TwoFactorChallengeDialog };
